import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddSubscriptionModal from '../components/AddSubscriptionModal';
import Pagination from '../components/Pagination';
import { useSettings } from '../context/SettingsContext';
import PlanSelect from '../components/PlanSelect';
import PhoneInput from '../components/PhoneInput';

const PAGE_SIZES = [10, 25, 50];
const fmt = (n) => (n != null && !isNaN(n) ? Number(n).toLocaleString() : '0');

function MonthStatusBadge({ status, t }) {
  if (status === 'paid')    return <span className="badge badge-green">✓ {t('paid')}</span>;
  if (status === 'partial') return <span className="badge" style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--accent)' }}>◑ {t('partial')}</span>;
  if (status === 'unpaid')  return <span className="badge badge-red">✗ {t('unpaid')}</span>;
  return <span className="badge badge-red">✗ {t('unpaid')}</span>;
}

export default function Restaurants() {
  const now = new Date();

  const [restaurants,    setRestaurants]    = useState([]);
  const [summary,        setSummary]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [selected,       setSelected]       = useState(null);
  const [editTarget,     setEditTarget]     = useState(null);
  const [allPartners,    setAllPartners]    = useState([]);
  const [pageSize,       setPageSize]       = useState(25);
  const [page,           setPage]           = useState(1);
  const [partnerFilter,  setPartnerFilter]  = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');

  // Month/year picker — defaults to current month
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  const [viewYear,  setViewYear]  = useState(now.getFullYear());

  const navigate = useNavigate();
  const { t }    = useSettings();
  const months      = t('months');
  const monthsShort = t('monthsShort');
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  useEffect(() => { setPage(1); }, [search, pageSize, partnerFilter, statusFilter, viewMonth, viewYear]);

  // Fetch all partner accounts for the edit modal dropdown
  useEffect(() => {
    axios.get('/api/auth/admins')
      .then(res => setAllPartners((res.data || []).filter(a => a.role === 'partner')))
      .catch(() => {});
  }, []);

  const fetchRestaurants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/restaurants?month=${viewMonth}&year=${viewYear}`);
      const data = res.data;
      // Handle both old array shape and new { restaurants, summary } shape
      if (Array.isArray(data)) {
        setRestaurants(data);
        setSummary(null);
      } else {
        setRestaurants(Array.isArray(data.restaurants) ? data.restaurants : []);
        setSummary(data.summary ?? null);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [viewMonth, viewYear]);

  useEffect(() => { fetchRestaurants(); }, [fetchRestaurants]);

  // Build unique partner list for dropdown
  const partnerOptions = useMemo(() => {
    const seen = new Map();
    (restaurants || []).forEach(r => { if (r.partnerId) seen.set(r.partnerId._id, r.partnerId); });
    return [...seen.values()];
  }, [restaurants]);

  const filtered = useMemo(() =>
    (restaurants || []).filter(r => {
      const matchSearch  = r.name.toLowerCase().includes(search.toLowerCase()) ||
                           (r.address || '').toLowerCase().includes(search.toLowerCase());
      const matchPartner = !partnerFilter ||
                           (partnerFilter === '__none__' ? !r.partnerId : String(r.partnerId?._id) === partnerFilter);
      const matchStatus  = !statusFilter ||
                           (statusFilter === 'none' ? !r.currentMonthStatus || r.currentMonthStatus === 'none'
                                                     : r.currentMonthStatus === statusFilter);
      return matchSearch && matchPartner && matchStatus;
    }), [restaurants, search, partnerFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

  const viewMonthName = monthsShort[viewMonth - 1];
  const isCurrentMonth = viewMonth === now.getMonth() + 1 && viewYear === now.getFullYear();

  // ── Inline Edit Modal ────────────────────────────────────
  const EditModal = () => {
    const [form, setForm] = React.useState({
      name:    editTarget?.name    || '',
      phoneNumber: editTarget?.phoneNumber || '',
      address: editTarget?.address || '',
      planId:     editTarget?.planId?._id   || editTarget?.planId   || '',
      partnerId:  editTarget?.partnerId?._id || editTarget?.partnerId || '',
    });
    const [saving, setSaving] = React.useState(false);
    const [err,    setErr]    = React.useState('');
    const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSave = async (e) => {
      e.preventDefault(); setErr(''); setSaving(true);
      try {
        await axios.put(`/api/restaurants/${editTarget._id}`, form);
        setEditTarget(null);
        fetchRestaurants();
      } catch (ex) { setErr(ex.response?.data?.message || 'Error'); }
      finally { setSaving(false); }
    };

    if (!editTarget) return null;
    return (
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditTarget(null)}>
        <div className="modal" style={{ maxWidth: 460 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
            <h2 style={{ fontSize: 22 }}>{t('editRestaurantInfo')}</h2>
            <button onClick={() => setEditTarget(null)} style={{ background: 'none', color: 'var(--text-muted)', fontSize: 22, lineHeight: 1 }}>×</button>
          </div>
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label className="form-label">{t('restaurant')} Name *</label>
              <input type="text" required value={form.name} onChange={e => setF('name', e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t('phoneNumber')}</label>
                <PhoneInput value={form.phoneNumber} onChange={v => setF('phoneNumber', v)} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('plan')}</label>
                <PlanSelect value={form.planId} onChange={(id) => setF('planId', id)} showPrice={false} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('partner')}</label>
              <select value={form.partnerId} onChange={e => setF('partnerId', e.target.value)}>
                <option value="">{t('noPartner')}</option>
                {allPartners.map(p => (
                  <option key={p._id} value={p._id}>
                    {p.username}{p.companyName ? ` (${p.companyName})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">{t('address')}</label>
              <input type="text" value={form.address} onChange={e => setF('address', e.target.value)} />
            </div>
            {err && <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 14, border: '1px solid rgba(224,92,92,0.2)' }}>{err}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={() => setEditTarget(null)} className="btn-ghost" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn-primary" style={{ flex: 2 }} disabled={saving}>{saving ? t('saving') : t('save')}</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 34, marginBottom: 6 }}>{t('restaurants')}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            {summary?.paid ?? 0} {t('paid')} · {summary?.partial ?? 0} {t('partial')} · {summary?.unpaid ?? 0} {t('unpaid')} — {viewMonthName} {viewYear}
            {isCurrentMonth && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--accent)' }}>● current</span>}
          </p>
        </div>

        {/* Month / Year picker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select className="select-control" value={viewMonth} onChange={e => setViewMonth(Number(e.target.value))} style={{ width: 120 }}>
            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="select-control" value={viewYear} onChange={e => setViewYear(Number(e.target.value))} style={{ width: 90 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {!isCurrentMonth && (
            <button className="btn-ghost" style={{ fontSize: 12, padding: '7px 12px' }}
              onClick={() => { setViewMonth(now.getMonth() + 1); setViewYear(now.getFullYear()); }}>
              ↺ Today
            </button>
          )}
        </div>
      </div>

      {/* Stats — count cards + money cards */}
      <div className="stats-grid" style={{ marginBottom: 28 }}>
        {/* Count stats */}
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 26, fontFamily: 'DM Serif Display', color: 'var(--accent)', marginBottom: 4 }}>
            {summary?.total ?? restaurants.length}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('totalRestaurants')}</div>
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 26, fontFamily: 'DM Serif Display', color: 'var(--green)', marginBottom: 4 }}>
            {summary?.paid ?? 0}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('paid')} — {viewMonthName}</div>
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 26, fontFamily: 'DM Serif Display', color: 'var(--accent)', marginBottom: 4 }}>
            {summary?.partial ?? 0}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('partial')} — {viewMonthName}</div>
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 26, fontFamily: 'DM Serif Display', color: 'var(--red)', marginBottom: 4 }}>
            {summary?.unpaid ?? 0}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('unpaidOverdue')} — {viewMonthName}</div>
        </div>

        {/* Money stats */}
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 20, fontFamily: 'DM Serif Display', color: 'var(--green)', marginBottom: 4, wordBreak: 'break-word' }}>
            {fmt(summary?.totalPaidAmount)} <span style={{ fontSize: 12 }}>UZS</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('collected')} — {viewMonthName} {viewYear}</div>
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 20, fontFamily: 'DM Serif Display', color: summary?.totalRemaining > 0 ? 'var(--red)' : 'var(--text-muted)', marginBottom: 4, wordBreak: 'break-word' }}>
            {fmt(summary?.totalRemaining)} <span style={{ fontSize: 12 }}>UZS</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('totalOutstanding')} — {viewMonthName} {viewYear}</div>
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 20, fontFamily: 'DM Serif Display', color: 'var(--accent)', marginBottom: 4, wordBreak: 'break-word' }}>
            {fmt(summary?.nextMonthExpected)} <span style={{ fontSize: 12 }}>UZS</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t('nextMonthExpected')} — {summary ? t('monthsShort')[summary.nextMonth - 1] : ''} {summary?.nextYear ?? ''}
          </div>
        </div>
      </div>

      {/* Search + partner filter + rows per page */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="search" placeholder={t('search')} value={search}
          onChange={e => setSearch(e.target.value)} style={{ maxWidth: 280, flex: '1 1 160px' }} />

        <select className="select-control" value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)} style={{ maxWidth: 200, flex: '1 1 140px' }}>
          <option value="">{t('allPartners')}</option>
          <option value="__none__">{t('noPartner')}</option>
          {partnerOptions.map(p => (
            <option key={p._id} value={p._id}>
              {p.username}{p.companyName ? ` (${p.companyName})` : ''}
            </option>
          ))}
        </select>

        {/* Status filter */}
        <select className="select-control" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ maxWidth: 180, flex: '1 1 130px' }}>
          <option value="">{t('allStatuses')}</option>
          <option value="paid">{"✓ " + t('paid')}</option>
          <option value="partial">{"◑ " + t('partial')}</option>
          <option value="none">{"✗ " + t('unpaid')}</option>
        </select>

        {(partnerFilter || statusFilter) && (
          <button onClick={() => { setPartnerFilter(''); setStatusFilter(''); }}
            style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '6px 10px', whiteSpace: 'nowrap', flexShrink: 0 }}>
            ✕ Clear
          </button>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{t('rowsPerPage')}:</span>
          <select className="select-control" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: 70 }}>
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card table-scroll" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {[t('restaurant'), t('plan'), t('partner'), t('phoneNumber'),
                `${viewMonthName} ${t('status')}`,
                `${t('paidAmount')}`, `${t('remainingAmount')}`,
                t('lastPayment'), t('actions')].map(h => (
                <th key={h} style={{ padding: '13px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('notFound')}</td></tr>
            ) : paginated.map(r => (
              <tr key={r._id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.12s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--table-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '13px 14px' }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{r.name}</div>
                  {r.address && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.address}</div>}
                </td>
                <td style={{ padding: '13px 14px' }}>
                  {r.planId ? <span className="badge badge-gold">{r.planId.name}</span> : <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>—</span>}
                </td>
                <td style={{ padding: '13px 14px' }}>
                  {r.partnerId ? (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#6ab0ff' }}>{r.partnerId.username}</div>
                      {r.partnerId.companyName && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 1 }}>{r.partnerId.companyName}</div>}
                    </div>
                  ) : <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>—</span>}
                </td>
                <td style={{ padding: '13px 14px', color: 'var(--text-muted)', fontSize: 13 }}>{r.phoneNumber || '—'}</td>
                <td style={{ padding: '13px 14px' }}>
                  <MonthStatusBadge status={r.currentMonthStatus} t={t} />
                </td>
                <td style={{ padding: '13px 14px', fontSize: 13, color: r.paidAmount > 0 ? 'var(--green)' : 'var(--text-dim)', fontWeight: r.paidAmount > 0 ? 600 : 400, whiteSpace: 'nowrap' }}>
                  {r.paidAmount > 0 ? fmt(r.paidAmount) + ' UZS' : '—'}
                </td>
                <td style={{ padding: '13px 14px', fontSize: 13, color: r.remainingAmount > 0 ? 'var(--red)' : 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                  {r.remainingAmount > 0 ? fmt(r.remainingAmount) + ' UZS' : '—'}
                </td>
                <td style={{ padding: '13px 14px', color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                  {r.lastPayment ? new Date(r.lastPayment).toLocaleDateString('en-GB') : '—'}
                </td>
                <td style={{ padding: '13px 14px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 11px' }} onClick={() => navigate(`/restaurants/${r._id}`)}>
                      {t('view')}
                    </button>
                    <button className="btn-primary" style={{ fontSize: 12, padding: '6px 11px' }} onClick={() => setSelected(r)}>
                      {t('pay')}
                    </button>
                    <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 11px' }} onClick={() => setEditTarget(r)}>
                      ✎
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Pagination page={page} totalPages={totalPages} total={filtered.length} pageSize={pageSize} onPage={setPage} />
      </div>

      <EditModal />

      {selected && (
        <AddSubscriptionModal
          restaurant={selected}
          onClose={() => setSelected(null)}
          onSuccess={() => { setSelected(null); fetchRestaurants(); }}
        />
      )}
    </div>
  );
}
